// hooks/useCategoryFilter.ts
import { useState, useEffect } from "react";
import { Category, SelectProduct, StylingStaff } from "../types/salon.types";

export const useCategoryFilter = (
  products: SelectProduct[] = [], // fallback empty array
  categories: Category[] = [],
  activeTab: "Staffs" | "Services",
  selectedStaff: StylingStaff | null
) => {
  const [filterProducts, setFilterProducts] = useState<SelectProduct[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");

  // Reset category filter when activeTab changes
  useEffect(() => {
    setFilterProducts([]);
    setSelectedCategoryId(null);
    setMessage("");
  }, [activeTab]);

  const filteredCategories = (categories || []).filter(
    (c: Category) =>
      c.type?.trim().toLowerCase() ===
      (activeTab === "Staffs" ? "services" : "products")
  );

  // Handle selecting category
  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryId(categoryId);

    // Safety check
    if (!Array.isArray(products) || products.length === 0) {
      setFilterProducts([]);
      setMessage("No products available. Please add products in Admin.");
      return;
    }

    // Filter products by category
    const productFilter = products.filter(
      (p) => p.categoryId === categoryId
    );

    // If active tab is staff and staff is selected
    if (activeTab === "Staffs" && selectedStaff) {
      const staffProducts = productFilter.filter(
        (p) =>
          Array.isArray(p.staffList) &&
          p.staffList.includes(selectedStaff.firstName)
      );

      setFilterProducts(staffProducts);

      if (staffProducts.length === 0) {
        setMessage(
          "No products assigned to this staff. Please add products in Admin."
        );
      } else {
        setMessage("");
      }

      return;
    }

    setFilterProducts(productFilter);

    // Empty category products message
    if (productFilter.length === 0) {
      setMessage(
        "No products found in this category. Please add products in Admin."
      );
    } else {
      setMessage("");
    }
  };

  // Reset category filter completely
  const resetCategoryFilter = () => {
    setFilterProducts([]);
    setSelectedCategoryId(null);
    setMessage("");
  };

  return {
    filterProducts,
    filteredCategories,
    selectedCategoryId,
    handleCategorySelect,
    resetCategoryFilter,
    message,
  };
};